import React from 'react';

import Badge from '@/components/Badge';
import TableRowButtons from '@/components/Common/TableRowButtons';
import { parseCountryIso2, wrapWithCountryFlag } from '@/pages/configurations/user-devices/utils';
import { DEFAULT_EMPTY_VALUE } from '@/utils/constants';
import {
  createEnumOptions,
  getEnumTranslationKey,
  getEnumValue,
  parseOptionalNumericEnumFromApi,
} from '@/utils/core/data-transformation/enumHelpers';
import { CoreEnums } from '@/utils/domain/constants';
import { createTableActionsColumn, formatTZLocal, getEnumNamespace } from '@/utils/infrastructure';
import { createHighlightRender } from '@/utils/infrastructure/highlightColumns';
import Filters, { FILTER_FIELD_SIZES, registerModuleFilters } from '@/utils/input/filters';
import { formatCurrency } from '@/utils/output/formatters';

import { MODEL_NAME } from './constants';

const { BaseFilters } = Filters;

export const formatMonthlyFeeWithCurrency = (monthlyFee, currency) => {
  const isoCurrency = getEnumValue(CoreEnums.Currency, currency) || 'USD';
  return formatCurrency(monthlyFee ?? 0, isoCurrency, {
    maximumFractionDigits: 2,
  });
};

const billingSubscriptionStatus = {
  [CoreEnums.BillingSubscriptionStatus.PendingActivation]: { status: 'info' },
  [CoreEnums.BillingSubscriptionStatus.PendingPayment]: { status: 'warning' },
  [CoreEnums.BillingSubscriptionStatus.Active]: { status: 'success' },
  [CoreEnums.BillingSubscriptionStatus.PastDue]: { status: 'execution' },
  [CoreEnums.BillingSubscriptionStatus.Canceled]: { status: 'canceled' },
  [CoreEnums.BillingSubscriptionStatus.Suspended]: { status: 'error' },
};

const StatusFilter = {
  ...BaseFilters.EnumFilter,
  name: 'status',
  label: 'global.text.status',
  enumNamespace: getEnumNamespace(CoreEnums.BillingSubscriptionStatus, 'Billing'),
  enumSource: CoreEnums.BillingSubscriptionStatus,
  options: createEnumOptions(CoreEnums.BillingSubscriptionStatus, 'billingSubscriptionStatus', {
    casing: 'camel',
  }),
  badgeMap: billingSubscriptionStatus,
  badgeType: 'group-item',
  filterSearch: true,
};

const CompanyFilter = {
  ...BaseFilters.RemoteFilter,
  name: 'company',
  label: 'global.text.company',
  labelInValue: true,
  displayField: 'commercialName',
  apiRoute: 'company/paged',
  filterSearch: true,
  params: {
    select: 'id,commercialName',
    filter: 'isActive eq true',
    orderBy: 'commercialName asc',
  },
  oDataField: 'id',
};

const BillingModeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'billingMode',
  label: 'global.text.billingMode',
  enumNamespace: getEnumNamespace(CoreEnums.ChannelBillingMode, 'Channel.Financial'),
  enumSource: CoreEnums.ChannelBillingMode,
  options: createEnumOptions(CoreEnums.ChannelBillingMode, 'channelBillingMode', {
    casing: 'camel',
  }),
};

const CurrencyFilter = {
  ...BaseFilters.EnumFilter,
  name: 'currency',
  label: 'global.text.currency',
  enumNamespace: getEnumNamespace(CoreEnums.Currency),
  enumSource: CoreEnums.Currency,
  options: createEnumOptions(CoreEnums.Currency, 'currency', {
    casing: 'lower',
  }),
};

const BillingCycleTypeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'billingCycleType',
  label: 'global.text.billingCycleType',
  enumNamespace: getEnumNamespace(CoreEnums.BillingCycleType, 'Billing'),
  enumSource: CoreEnums.BillingCycleType,
  options: createEnumOptions(CoreEnums.BillingCycleType, 'billingCycleType', {
    casing: 'camel',
  }),
};

const PaymentCollectionModeFilter = {
  ...BaseFilters.EnumFilter,
  name: 'paymentCollectionMode',
  label: 'global.text.paymentCollectionMode',
  enumNamespace: getEnumNamespace(CoreEnums.BillingCollectionMode, 'Billing'),
  enumSource: CoreEnums.BillingCollectionMode,
  options: createEnumOptions(CoreEnums.BillingCollectionMode, 'billingCollectionMode', {
    casing: 'camel',
  }),
};

const NextBillingDateFilter = {
  ...Filters.DateFilter,
  name: 'nextBillingDate',
  label: 'global.text.nextBillingDate',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

const AutoRenewFilter = {
  ...BaseFilters.BooleanFilter,
  name: 'autoRenew',
  label: 'global.text.autoRenew',
  badgeOptions: {
    true: { status: 'success' },
    false: { status: 'default' },
  },
  badgeType: 'group-item',
  filterSize: FILTER_FIELD_SIZES.MEDIUM,
};

const CreatedDateFilter = {
  ...Filters.DateFilter,
  name: 'created',
  label: 'global.created',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

const UpdatedDateFilter = {
  ...Filters.DateFilter,
  name: 'updated',
  label: 'global.updated',
  defaultValue: CoreEnums.FilterDateRange.Disabled,
};

export const filtersConfig = [
  StatusFilter,
  CompanyFilter,
  BillingModeFilter,
  CurrencyFilter,
  BillingCycleTypeFilter,
  PaymentCollectionModeFilter,
  NextBillingDateFilter,
  AutoRenewFilter,
  CreatedDateFilter,
  UpdatedDateFilter,
];

registerModuleFilters(MODEL_NAME, filtersConfig);

/**
 * Domain detail mapping: list-matching badges for status/autoRenew,
 * monthly fee + currency, country flag. Other enum selects use `_form.js` locale.
 */
export function mapBillingSubscriptionDetailFields(rows, { item }) {
  if (!item || !rows?.length) return rows;

  return rows.map((row) => {
    if (row.name === 'status') {
      const resolved = parseOptionalNumericEnumFromApi(
        CoreEnums.BillingSubscriptionStatus,
        item.status,
      );
      const config = resolved !== undefined ? billingSubscriptionStatus[resolved] : null;
      return {
        ...row,
        badgeStatus: config?.status || 'default',
        badgeType: 'group-item',
      };
    }

    if (row.name === 'autoRenew') {
      const autoRenew = item.autoRenew === true;
      return {
        ...row,
        type: 'badge',
        badgeType: 'group-item',
        badgeStatus: autoRenew ? 'success' : 'default',
        value: autoRenew ? 'global.text.yes' : 'global.text.no',
        locale: true,
      };
    }

    if (row.name === 'monthlyFee') {
      if (item.monthlyFee == null) {
        return { ...row, value: null };
      }
      return {
        ...row,
        value: formatMonthlyFeeWithCurrency(item.monthlyFee, item.currency),
      };
    }

    if (row.name === 'billingCountry') {
      const country = item.billingCountry;
      const name = country?.name;
      if (!name) {
        return { ...row, value: null };
      }
      const iso2 = parseCountryIso2(country?.code);
      return { ...row, value: wrapWithCountryFlag(iso2, name) };
    }

    return row;
  });
}

export function buildBillingSubscriptionColumns({
  formatMessage,
  openDrawerCreateOrEdit,
  searchCriteria,
}) {
  const highlight = createHighlightRender(searchCriteria);

  const renderEnumBadge = (enumObject, value, translationPrefix) => {
    const resolved = parseOptionalNumericEnumFromApi(enumObject, value);
    if (resolved === undefined) {
      return <Badge status="default" type="process" text={DEFAULT_EMPTY_VALUE} />;
    }
    return (
      <Badge
        status="default"
        type="process"
        text={formatMessage({
          id: getEnumTranslationKey(enumObject, resolved, translationPrefix),
        })}
      />
    );
  };

  return [
    {
      key: 'status',
      title: formatMessage({ id: 'global.text.status' }),
      dataIndex: 'status',
      align: 'center',
      sorter: true,
      render: (value) => {
        const resolved = parseOptionalNumericEnumFromApi(
          CoreEnums.BillingSubscriptionStatus,
          value,
        );
        const config = resolved !== undefined ? billingSubscriptionStatus[resolved] : null;
        if (!config) {
          return <Badge status="default" type="group-item" text={DEFAULT_EMPTY_VALUE} />;
        }
        return (
          <Badge
            status={config.status}
            type="group-item"
            text={formatMessage({
              id: getEnumTranslationKey(
                CoreEnums.BillingSubscriptionStatus,
                resolved,
                'billingSubscriptionStatus',
              ),
            })}
          />
        );
      },
    },
    {
      key: 'automyPlan/name',
      title: formatMessage({ id: 'global.text.plan' }),
      dataIndex: ['automyPlan', 'name'],
      sorter: true,
      render: highlight,
    },
    {
      key: 'company/commercialName',
      title: formatMessage({ id: 'global.text.company' }),
      dataIndex: ['company', 'commercialName'],
      sorter: true,
      render: highlight,
    },
    {
      key: 'billingMode',
      title: formatMessage({ id: 'global.text.billingMode' }),
      dataIndex: 'billingMode',
      align: 'center',
      sorter: true,
      render: (value) => renderEnumBadge(CoreEnums.ChannelBillingMode, value, 'channelBillingMode'),
    },
    {
      key: 'monthlyFee',
      title: formatMessage({ id: 'global.text.monthlyFee' }),
      dataIndex: 'monthlyFee',
      align: 'right',
      sorter: true,
      render: (_, record) => formatMonthlyFeeWithCurrency(record.monthlyFee, record.currency),
    },
    {
      key: 'billingCycleType',
      title: formatMessage({ id: 'global.text.billingCycleType' }),
      dataIndex: 'billingCycleType',
      align: 'center',
      sorter: true,
      render: (value) => renderEnumBadge(CoreEnums.BillingCycleType, value, 'billingCycleType'),
    },
    {
      key: 'paymentCollectionMode',
      title: formatMessage({ id: 'global.text.paymentCollectionMode' }),
      dataIndex: 'paymentCollectionMode',
      align: 'center',
      sorter: true,
      render: (value) =>
        renderEnumBadge(CoreEnums.BillingCollectionMode, value, 'billingCollectionMode'),
    },
    {
      key: 'nextBillingDate',
      title: formatMessage({ id: 'global.text.nextBillingDate' }),
      dataIndex: 'nextBillingDate',
      sorter: true,
      render: (value) => (value ? formatTZLocal(value) : DEFAULT_EMPTY_VALUE),
    },
    {
      key: 'autoRenew',
      title: formatMessage({ id: 'global.text.autoRenew' }),
      dataIndex: 'autoRenew',
      align: 'center',
      sorter: true,
      render: (value) => (
        <Badge
          status={value ? 'success' : 'default'}
          type="group-item"
          text={formatMessage({ id: value ? 'global.text.yes' : 'global.text.no' })}
        />
      ),
    },
    createTableActionsColumn({
      inlineActionCount: 1,
      render: (record) => (
        <TableRowButtons
          onDetail={() => openDrawerCreateOrEdit(record.id, true)}
          formatMessage={formatMessage}
          actionOrder={{ primary: ['detail'], secondary: [] }}
        />
      ),
    }),
  ];
}
